// Auth.tsx
import React, { FC, createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Environment } from '../Config/constants';
import { useNavigate } from 'react-router-dom';
import { StorageLocalstorage } from '../Shared/storage/storage-localstorage';
import { onPasswordResetRequired, resetPasswordResetNotification } from '../Services/password-reset-handler';

export interface UserProps {
  username: string;
  permissions: string[]
  permissionsScreens: string[],
  resetPassword: boolean

}

interface AuthData {
  token: string | null;
  user: UserProps | null;
}

interface AuthProps {
  user: UserProps | null;
  isAuthenticated: boolean;
  signIn: (token: string, user: UserProps) => void;
  signOut: () => void;
  updateUser: (newUser: UserProps) => void;
  markPasswordResetRequired: () => void;
}

const AuthContext = createContext<AuthProps | undefined>(undefined);

export const AuthProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate()
  // Estado para armazenar token e usuário
  const [data, setData] = useState<AuthData>(() => {
    const token = localStorage.getItem(`${Environment.CUSTOMER}:token`);
    const user = localStorage.getItem(`${Environment.CUSTOMER}:user`);

    if (token && user) {
 
      try {
        return { token, user: JSON.parse(user) };
      } catch (error) {
        console.error('Erro ao fazer parse do usuário do localStorage:', error);
        return { token: null, user: null };
      }
    }

    return { token: null, user: null };
  });

  const signIn = useCallback((token: string, user: UserProps): void => {
    localStorage.setItem(`${Environment.CUSTOMER}:token`, token);
    localStorage.setItem(`${Environment.CUSTOMER}:user`, JSON.stringify(user));
    setData({ token, user });
  }, []);

  const signOut = useCallback(async (): Promise<void> => {
    StorageLocalstorage.clearAll()
    resetPasswordResetNotification();
    setData({ token: null, user: null });
    navigate('/signin')
  }, [navigate]);

  const updateUser = useCallback((newUser: UserProps): void => {
    setData((prev) => {
      localStorage.setItem(`${Environment.CUSTOMER}:user`, JSON.stringify(newUser));
      return { token: prev.token, user: newUser };
    });
  }, []);

  const markPasswordResetRequired = useCallback((): void => {
    setData((prev) => {
      if (!prev.user || prev.user.resetPassword) {
        return prev;
      }

      const newUser = { ...prev.user, resetPassword: true };
      localStorage.setItem(`${Environment.CUSTOMER}:user`, JSON.stringify(newUser));
      return { token: prev.token, user: newUser };
    });
  }, []);

  useEffect(() => {
    return onPasswordResetRequired(() => {
      markPasswordResetRequired();
    });
  }, [markPasswordResetRequired]);

  const isAuthenticated = !!data.token;

  return (
    <AuthContext.Provider value={{ 
      user: data.user, 
      isAuthenticated,
      signIn,
      signOut,
      updateUser,
      markPasswordResetRequired,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthProps => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }

  return context;
};
